import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

import { scheduleRefusalMessage, scheduleRefusals } from "./scheduleRefusals";
import { withJWTClient } from "../connectClient";
import { gatewayUrl } from "../gateway";

import {
  listMarketingCampaignsResponseSchema,
  listMarketingEventTypesResponseSchema,
  marketingCampaignSchema,
  marketingEventTypeSchema,
  type CreateMarketingCampaignPayload,
  type ListMarketingCampaignsResponse,
  type ListMarketingEventTypesResponse,
  type MarketingBroadcast,
  type MarketingCampaign,
  proposeMarketingCampaignResponseSchema,
  type MarketingEventType,
  type ProposeMarketingCampaignPayload,
  type ProposeMarketingCampaignResponse,
  type UpdateMarketingCampaignPayload,
} from "@/features/MarketingCalendar/schemas";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// --- Query Keys ---

export const marketingCalendarKeys = {
  all: (siteId: string) => ["marketingCalendar", siteId] as const,
  campaigns: (siteId: string) =>
    [...marketingCalendarKeys.all(siteId), "campaigns"] as const,
  campaignsByMonth: (siteId: string, month: string) =>
    [...marketingCalendarKeys.campaigns(siteId), month] as const,
  campaignDetail: (siteId: string, campaignId: string) =>
    [...marketingCalendarKeys.campaigns(siteId), "detail", campaignId] as const,
  eventTypes: (siteId: string) =>
    [...marketingCalendarKeys.all(siteId), "eventTypes"] as const,
};

// --- API Functions ---
// Responses are zod-parsed so the mocks (and later the real endpoints) are
// held to the same contract; a drift fails loudly instead of rendering junk.

/** @param month optional "YYYY-MM" filter */
export async function getMarketingCampaigns(
  siteId: string,
  month?: string,
): Promise<ListMarketingCampaignsResponse> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(gatewayUrl(siteId, "marketing-calendar", "campaigns"), {
      params: month ? { month } : undefined,
    }),
  );
  return listMarketingCampaignsResponseSchema.parse(response.data);
}

export async function getMarketingCampaign(
  siteId: string,
  campaignId: string,
): Promise<MarketingCampaign> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "campaigns", campaignId),
    ),
  );
  return marketingCampaignSchema.parse(response.data);
}

export async function createMarketingCampaign(
  siteId: string,
  payload: CreateMarketingCampaignPayload,
): Promise<MarketingCampaign> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "campaigns"),
      payload,
    ),
  );
  return marketingCampaignSchema.parse(response.data);
}

export async function updateMarketingCampaign(
  siteId: string,
  campaignId: string,
  payload: UpdateMarketingCampaignPayload,
): Promise<MarketingCampaign> {
  const response = await withJWTClient((client) =>
    client.patch<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "campaigns", campaignId),
      payload,
    ),
  );
  return marketingCampaignSchema.parse(response.data);
}

/** Activate a draft campaign: the backend flips it to "scheduled" and fans
 * out activation to its broadcasts, returning the full updated campaign. */
export async function scheduleMarketingCampaign(
  siteId: string,
  campaignId: string,
): Promise<MarketingCampaign> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(
      gatewayUrl(
        siteId,
        "marketing-calendar",
        "campaigns",
        `${campaignId}:schedule`,
      ),
    ),
  );
  return marketingCampaignSchema.parse(response.data);
}

/** Return a scheduled campaign to "draft": unsent broadcasts go back to
 * draft (already-sent ones stay sent), returning the full updated campaign. */
export async function unscheduleMarketingCampaign(
  siteId: string,
  campaignId: string,
): Promise<MarketingCampaign> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(
      gatewayUrl(
        siteId,
        "marketing-calendar",
        "campaigns",
        `${campaignId}:unschedule`,
      ),
    ),
  );
  return marketingCampaignSchema.parse(response.data);
}

/** A non-empty rejectionReason turns the delete into a rejection: the
 * backend stores it on the soft-deleted row for Blastro to read (CON-188). */
export async function deleteMarketingCampaign(
  siteId: string,
  campaignId: string,
  rejectionReason?: string,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete(
      gatewayUrl(siteId, "marketing-calendar", "campaigns", campaignId),
      rejectionReason ? { params: { rejection_reason: rejectionReason } } : {},
    ),
  );
}

/** Fire-and-acknowledge: Blastro generates async off a 202 receipt (no
 * status API). Never auto-retry an accepted call: the webhook has no dedup. */
export async function proposeMarketingCampaign(
  siteId: string,
  payload: ProposeMarketingCampaignPayload,
): Promise<ProposeMarketingCampaignResponse> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "campaigns:propose"),
      payload,
    ),
  );
  return proposeMarketingCampaignResponseSchema.parse(response.data);
}

/** Picker source: the built-in defaults unioned with the site's own types. */
export async function getMarketingEventTypes(
  siteId: string,
): Promise<ListMarketingEventTypesResponse> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "event-types"),
    ),
  );
  return listMarketingEventTypesResponseSchema.parse(response.data);
}

/** Add a custom event type for the site. The backend trims and matches
 * case-insensitively, so the returned name is the canonical spelling (a name
 * equal to a default echoes the default back without creating a row). */
export async function createMarketingEventType(
  siteId: string,
  name: string,
): Promise<MarketingEventType> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(
      gatewayUrl(siteId, "marketing-calendar", "event-types"),
      { name },
    ),
  );
  return marketingEventTypeSchema.parse(response.data);
}

// --- Query Options ---

// Always stale (overrides the app's 5-minute default): campaign reads are a
// live join over broadcasts edited elsewhere in the app (CON-186), so every
// mount and window focus refetches instead of serving a cached join.
export const marketingCampaignsQueryOptions = (
  siteId: string,
  month: string,
) => ({
  queryKey: marketingCalendarKeys.campaignsByMonth(siteId, month),
  queryFn: () => getMarketingCampaigns(siteId, month),
  staleTime: 0,
});

export const marketingCampaignQueryOptions = (
  siteId: string,
  campaignId: string,
) => ({
  queryKey: marketingCalendarKeys.campaignDetail(siteId, campaignId),
  queryFn: () => getMarketingCampaign(siteId, campaignId),
  staleTime: 0,
});

export const marketingEventTypesQueryOptions = (siteId: string) => ({
  queryKey: marketingCalendarKeys.eventTypes(siteId),
  queryFn: () => getMarketingEventTypes(siteId),
});

// --- Hooks ---

/** @param month "YYYY-MM" the calendar is showing */
export function useMarketingCampaigns(siteId: string, month: string) {
  return useQuery(marketingCampaignsQueryOptions(siteId, month));
}

export function useMarketingCampaign(siteId: string, campaignId: string) {
  return useQuery(marketingCampaignQueryOptions(siteId, campaignId));
}

export function useMarketingEventTypes(siteId: string) {
  return useQuery(marketingEventTypesQueryOptions(siteId));
}

export function useCreateMarketingEventType(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createMarketingEventType(siteId, name),
    onSuccess: (eventType) => {
      enqueueSnackbar(`Event type "${eventType.name}" added`, {
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.eventTypes(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to add event type");
    },
  });
}

export function useCreateMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMarketingCampaignPayload) =>
      createMarketingCampaign(siteId, payload),
    onSuccess: (campaign) => {
      enqueueSnackbar(`Campaign "${campaign.name}" created`, {
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error) => {
      // Create-as-scheduled hits the same all-or-nothing gate (CON-310): the
      // backend discards the draft it wrote, so nothing was created. Names
      // aren't on the wire here; the reasons carry the context.
      const refusals = scheduleRefusals(error);
      if (refusals.length > 0) {
        enqueueSnackbar(
          scheduleRefusalMessage("The campaign was not created", refusals),
          {
            variant: "error",
            style: { whiteSpace: "pre-line" },
          },
        );
        return;
      }
      enqueueErrorSnackbar(error, "Failed to create campaign");
    },
  });
}

export function useUpdateMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      payload,
    }: {
      campaignId: string;
      payload: UpdateMarketingCampaignPayload;
    }) => updateMarketingCampaign(siteId, campaignId, payload),
    onSuccess: (campaign) => {
      enqueueSnackbar(`Campaign "${campaign.name}" updated`, {
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update campaign");
    },
  });
}

export function useProposeMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProposeMarketingCampaignPayload) =>
      proposeMarketingCampaign(siteId, payload),
    onSuccess: () => {
      // Persistent on purpose: with no status API or notification, this
      // toast is the only signal a generation is running (X dismisses).
      enqueueSnackbar(
        "Blastro is planning a campaign. It will appear on the calendar shortly.",
        { variant: "success", persist: true },
      );
      // The 202 precedes Blastro's write: this refetch catches fast
      // generations, a later visit catches slow ones (no status API).
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error) => {
      // 503 = connect-api has no BLASTRO_API_KEY yet: proposer switched off.
      if (isAxiosError(error) && error.response?.status === 503) {
        enqueueSnackbar(
          "Campaign generation isn't live yet. The Blastro connection is still being set up.",
          { variant: "warning" },
        );
        return;
      }
      enqueueErrorSnackbar(error, "Failed to start campaign generation");
    },
  });
}

export function useScheduleMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) =>
      scheduleMarketingCampaign(siteId, campaignId),
    onSuccess: (campaign) => {
      enqueueSnackbar(`Campaign "${campaign.name}" scheduled`, {
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error, campaignId) => {
      // Schedule is all-or-nothing (CON-310): a refusal names each blocked
      // send and the campaign stays a draft, so the user can fix and retry.
      const refusals = scheduleRefusals(error);
      if (refusals.length > 0) {
        enqueueSnackbar(
          scheduleRefusalMessage(
            "The campaign stays a draft",
            refusals,
            cachedCampaignBroadcasts(queryClient, siteId, campaignId),
          ),
          { variant: "error", style: { whiteSpace: "pre-line" } },
        );
        return;
      }
      enqueueErrorSnackbar(error, "Failed to schedule campaign");
    },
  });
}

/** Broadcast id -> name source for refusal copy, from whatever month lists
 * are cached. The campaigns key also roots single-campaign entries, so the
 * shape is narrowed per entry; a cold cache means generic names, no fetch. */
function cachedCampaignBroadcasts(
  queryClient: QueryClient,
  siteId: string,
  campaignId: string,
): MarketingBroadcast[] {
  for (const [, data] of queryClient.getQueriesData<unknown>({
    queryKey: marketingCalendarKeys.campaigns(siteId),
  })) {
    const campaigns = (data as Partial<ListMarketingCampaignsResponse> | null)
      ?.campaigns;
    const campaign = Array.isArray(campaigns)
      ? campaigns.find((c) => c.id === campaignId)
      : undefined;
    if (campaign) return campaign.broadcasts;
  }
  return [];
}

export function useUnscheduleMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) =>
      unscheduleMarketingCampaign(siteId, campaignId),
    onSuccess: (campaign) => {
      enqueueSnackbar(`Campaign "${campaign.name}" unscheduled`, {
        variant: "success",
      });
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to unschedule campaign");
    },
  });
}

export function useDeleteMarketingCampaign(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      rejectionReason,
    }: {
      campaignId: string;
      rejectionReason?: string;
    }) => deleteMarketingCampaign(siteId, campaignId, rejectionReason),
    onSuccess: (_, { rejectionReason }) => {
      enqueueSnackbar(
        rejectionReason ? "Campaign rejected" : "Campaign deleted",
        { variant: "success" },
      );
      void queryClient.invalidateQueries({
        queryKey: marketingCalendarKeys.campaigns(siteId),
      });
    },
    onError: (error, { rejectionReason }) => {
      enqueueErrorSnackbar(
        error,
        rejectionReason
          ? "Failed to reject campaign"
          : "Failed to delete campaign",
      );
    },
  });
}
